import { useEffect, useState } from "react";
import {
  Alert, Box, Button, Card, Chip, Snackbar, Stack, Tab, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tabs, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LogoutIcon from "@mui/icons-material/Logout";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { useLanguage } from "../i18n/LanguageContext";
import { visitsApi } from "../api/resources";
import { COLORS } from "../theme";
import VisitDialog from "../components/visits/VisitDialog";
import ExitDialog from "../components/visits/ExitDialog";
import VisitHistoryTable from "../components/visits/VisitHistoryTable";

export default function VisitantesPage() {
  const { t, lang } = useLanguage();
  const [tab, setTab] = useState("active");
  const [active, setActive] = useState([]);
  const [showRegister, setShowRegister] = useState(false);
  const [exitFor, setExitFor] = useState(null);
  const [toast, setToast] = useState(null);

  const fmtTime = (iso) => new Date(iso).toLocaleTimeString(lang === "en" ? "en-US" : "es-MX", { hour: "2-digit", minute: "2-digit" });

  const loadActive = () => visitsApi.listActive().then(setActive);
  useEffect(() => {
    loadActive();
  }, []); // eslint-disable-line

  const handleRegister = async (data) => {
    await visitsApi.create(data);
    setShowRegister(false);
    setToast(t("visitantes.registered", data.name));
    loadActive();
  };

  const handleConfirmExit = async (id, data) => {
    await visitsApi.registerExit(id, data);
    setToast(t("visitantes.exitRegistered", exitFor.name));
    setExitFor(null);
    loadActive();
  };

  return (
    <Box>
      <Typography variant="h5">{t("visitantes.title")}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("visitantes.subtitle")}
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="active" label={`${t("visitantes.tabActive")}${active.length > 0 ? ` (${active.length})` : ""}`} />
        <Tab value="history" label={t("visitantes.tabHistory")} />
      </Tabs>

      {tab === "active" && (
        <Box>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowRegister(true)}>
              {t("visitantes.registerVisit")}
            </Button>
          </Stack>

          <TableContainer component={Card}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#F4F5EF" }}>
                  <TableCell>{t("visitantes.colVisitor")}</TableCell>
                  <TableCell>{t("visitantes.colContactReason")}</TableCell>
                  <TableCell>{t("visitantes.colEntry")}</TableCell>
                  <TableCell align="right">{t("panel.colAction")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {active.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 5, color: "text.secondary" }}>
                      {t("visitantes.noActive")}
                    </TableCell>
                  </TableRow>
                )}
                {active.map((v) => (
                  <TableRow key={v._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{v.name}</Typography>
                      {v.isProvider && (
                        <Chip
                          size="small"
                          icon={<LocalShippingIcon sx={{ fontSize: 12 }} />}
                          label={t("visitantes.provider")}
                          sx={{ height: 18, fontSize: 10, bgcolor: COLORS.brassSoft, color: "#8A5B18", mt: 0.3 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {v.contactId ? `${v.contactId.name} · ${v.contactId.unit}` : v.isProvider ? "—" : t("visitantes.noContact")}
                      </Typography>
                      {v.reason && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {v.reason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{fmtTime(v.entryAt)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" startIcon={<LogoutIcon />} onClick={() => setExitFor(v)}>
                        {t("visitantes.registerExit")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {tab === "history" && <VisitHistoryTable />}

      <VisitDialog open={showRegister} onClose={() => setShowRegister(false)} onSave={handleRegister} />
      <ExitDialog visit={exitFor} onClose={() => setExitFor(null)} onConfirm={handleConfirmExit} />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity="success" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
